import { Navigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import LoadingScreen from "./LoadingScreen";

const INTERNAL_ROLES = ["admin", "recruiter", "account_manager", "sales", "api_access"];
const PORTAL_URL = process.env.REACT_APP_CANDIDATE_PORTAL_URL || "https://portal.aiarchitech.com";

function mergeRoles(appMeta) {
  const arr = Array.isArray(appMeta?.roles) ? [...appMeta.roles] : [];
  const legacy = appMeta?.role;
  if (legacy && !arr.includes(legacy)) arr.push(legacy);
  return arr;
}

export default function ProtectedRoute({ children, allowedRoles = INTERNAL_ROLES }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let mounted = true;
    let initialResolved = false;

    async function resolveAuth(sess, isInitial) {
      if (!mounted) return;
      setSession(sess);
      if (!sess?.user) {
        setAuthorized(false);
        if (isInitial) setLoading(false);
        return;
      }

      const jwtRoles = mergeRoles(sess.user.app_metadata);
      if (jwtRoles.some((r) => allowedRoles.includes(r))) {
        setAuthorized(true);
        if (isInitial) setLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from("user_profile")
        .select("role, roles")
        .eq("id", sess.user.id)
        .maybeSingle();
      if (!mounted) return;

      const profileRoles = Array.isArray(profile?.roles) && profile.roles.length > 0
        ? profile.roles
        : profile?.role ? [profile.role] : [];

      if (profileRoles.some((r) => allowedRoles.includes(r))) {
        setAuthorized(true);
        if (isInitial) setLoading(false);
        return;
      }

      if (allowedRoles.includes("admin")) {
        const { data: adminRow } = await supabase
          .from("admins")
          .select("id")
          .eq("id", sess.user.id)
          .maybeSingle();
        if (!mounted) return;
        if (adminRow) {
          setAuthorized(true);
          if (isInitial) setLoading(false);
          return;
        }
      }

      setAuthorized(false);
      if (isInitial) setLoading(false);
    }

    supabase.auth.getSession().then(({ data }) => {
      initialResolved = true;
      resolveAuth(data?.session, true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (!initialResolved) return;
      resolveAuth(sess, false);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [allowedRoles]);

  if (loading) return <LoadingScreen message="Loading..." variant="light" />;
  if (!session) return <Navigate to="/login" replace />;
  if (!authorized) {
    const allRoles = mergeRoles(session.user?.app_metadata);
    const isCandidateOnly = allRoles.length > 0 && !allRoles.some((r) => INTERNAL_ROLES.includes(r));
    if (isCandidateOnly) {
      window.location.replace(PORTAL_URL);
      return null;
    }
    return <Navigate to="/" replace />;
  }

  return children || <Outlet />;
}
